import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  isPitchAboutYourGameValid,
  isPitchAboutYourselfValid,
  isPitchAskForXboxValid,
  isPitchInformationPublisherDeveloperValid,
} from "../../helpers/formValidation";
import { comparePitchApplicationStepStatus } from "../../helpers/pitchStatusHelpers";
import {
  PitchInformationAboutYourGameDataType,
  PitchInformationAboutYourselfDataType,
  PitchInformationAskForXboxDataType,
  PitchInformationPublisherDeveloperDataType,
} from "../../models/forms";
import { PitchApplicationStep } from "../../models/status";
import {
  PitchInformationFormId,
  PitchInformationSubFormsId,
} from "../../pages/PitchInformationPage/PitchInformationPageConstants";
import { OnboardingRootState } from "../../redux/reducers/rootReducer";
import { getPitchApplicationStep, getSubFormData } from "../../redux/selectors";

const stepPercentageValue = 25;
const increasePercentageIfValid = (isFormValid: boolean, currentPercentage: number): number =>
  isFormValid ? currentPercentage + stepPercentageValue : currentPercentage;

export const usePitchPercentage = () => {
  const [percentage, setPercentage] = useState(0);
  const currentStep = useSelector(getPitchApplicationStep) ?? PitchApplicationStep.Unset;

  // ID@Xbox Game Information sub form data
  const pitchPublisherDeveloperSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(
      state,
      PitchInformationFormId,
      PitchInformationSubFormsId.PublisherDeveloperInformation
    )
  ) as PitchInformationPublisherDeveloperDataType;
  const pitchAboutYourGameSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, PitchInformationFormId, PitchInformationSubFormsId.AboutYourGame)
  ) as PitchInformationAboutYourGameDataType;
  const pitchAskForXboxSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, PitchInformationFormId, PitchInformationSubFormsId.AskForXbox)
  ) as PitchInformationAskForXboxDataType;
  const pitchAboutYourselfSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, PitchInformationFormId, PitchInformationSubFormsId.AboutYourself)
  ) as PitchInformationAboutYourselfDataType;

  useEffect(() => {
    const calculatePercentage = async () => {
      if (currentStep === PitchApplicationStep.Unset) {
        setPercentage(0);
        return;
      }

      const applicationReadyOrSent =
        comparePitchApplicationStepStatus(
          currentStep,
          PitchApplicationStep.ApplicationReadyForReview
        ) >= 0;
      if (applicationReadyOrSent) {
        setPercentage(100);
        return;
      }

      const isPitchPublisherDeveloperSubFormDataValid =
        (await isPitchInformationPublisherDeveloperValid(pitchPublisherDeveloperSubFormData)) &&
        pitchPublisherDeveloperSubFormData.isStarted;
      const isPitchAboutYourGameSubFormDataValid =
        (await isPitchAboutYourGameValid(pitchAboutYourGameSubFormData)) &&
        pitchAboutYourGameSubFormData.isStarted;
      const isPitchAskForXboxSubFormDataValid =
        (await isPitchAskForXboxValid(pitchAskForXboxSubFormData)) &&
        pitchAskForXboxSubFormData.isStarted;
      const isPitchAboutYourselfSubFormDataValid =
        (await isPitchAboutYourselfValid(pitchAboutYourselfSubFormData)) &&
        pitchAboutYourselfSubFormData.isStarted;

      let tempPercentage = 0;
      tempPercentage = increasePercentageIfValid(
        isPitchPublisherDeveloperSubFormDataValid,
        tempPercentage
      );
      tempPercentage = increasePercentageIfValid(
        isPitchAboutYourGameSubFormDataValid,
        tempPercentage
      );
      tempPercentage = increasePercentageIfValid(isPitchAskForXboxSubFormDataValid, tempPercentage);
      tempPercentage = increasePercentageIfValid(
        isPitchAboutYourselfSubFormDataValid,
        tempPercentage
      );

      setPercentage(tempPercentage);
    };

    calculatePercentage();
  }, [
    currentStep,
    pitchAboutYourGameSubFormData,
    pitchAboutYourselfSubFormData,
    pitchAskForXboxSubFormData,
    pitchPublisherDeveloperSubFormData,
  ]);

  return percentage;
};
